import { toSentenceCaseLabel } from "../sentenceCaseLabel";

// Rule attributes are sourced from `finpubPrimaryFields` in production.

/** Rule-editor data type derived from semantic-layer attribute metadata. */
export type AttributeDataType =
  "NUMBER" | "CURRENCY" | "PERCENTAGE" | "TEXT" | "DATE" | "TIME" | "BOOLEAN";

export type QueryAttributeLike = {
  name: string;
  displayName?: string | null;
  logicalName?: string | null;
  tableName?: string | null;
  type?: string | null;
  filterable?: boolean | null;
  supportedLevels?: ReadonlyArray<string> | null;
};

export type AttributeLeaf = {
  /** Whether rule clauses may filter on the column. The catalogue keeps
   * non-filterable columns for metadata lookups — clause labels, type
   * inference, level support — but attribute pickers must scope to
   * `filterable` leaves (see `RulesSection`). */
  filterable: boolean;
  /** Canonical `<table>.<column>` id, e.g. `gl_account.is_active`. Sent
   * verbatim as `RuleConditionInput.attribute`. */
  path: string;
  /** Snake-case attribute name shown inside the leaf row, e.g. `is_active`. */
  label: string;
  /** Breadcrumb shown in the rule pill, e.g. `gl_account: is_active`. */
  pillLabel: string;
  /** Source label (kept for grouping/debugging; not displayed today). */
  database: string;
  /** Table label — first level of the tree, e.g. `gl_account`. */
  table: string;
  /** Rule-editor type, mapped from the semantic-layer attribute type. */
  dataType: AttributeDataType;
  /** Optional discrete-value catalogue. Real query attributes do not expose
   * this yet, so production leaves usually render free-text inputs. */
  values: ReadonlyArray<{ value: string; label: string }>;
  /** Level identifiers the column's Semantic Layer mappings support (e.g.
   * `["position", "transaction"]`). Empty/absent when the catalogue carries
   * no mapping data — consumers fall back to legacy level behavior. */
  supportedLevels?: ReadonlyArray<string>;
};

const ATTRIBUTE_TYPE_TO_DATA_TYPE: Readonly<Record<string, AttributeDataType>> = {
  BOOLEAN: "BOOLEAN",
  BOOL: "BOOLEAN",
  CURRENCY: "CURRENCY",
  DATE: "TIME",
  DATETIME: "TIME",
  DECIMAL: "NUMBER",
  DOUBLE: "NUMBER",
  FLOAT: "NUMBER",
  INTEGER: "NUMBER",
  NUMBER: "NUMBER",
  NUMERIC: "NUMBER",
  PERCENT: "PERCENTAGE",
  PERCENTAGE: "PERCENTAGE",
  STRING: "TEXT",
  TEXT: "TEXT",
  TIME: "TIME",
  TIMESTAMP: "TIME",
};

const BOOLEAN_VALUES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

const toDataType = (type: string | null | undefined): AttributeDataType =>
  type ? (ATTRIBUTE_TYPE_TO_DATA_TYPE[type.toUpperCase()] ?? "TEXT") : "TEXT";

export const toCanonicalAttributePath = (
  name: string,
  tableName: string | null | undefined,
): string => {
  if (name.includes(".")) return name;
  return tableName ? `${tableName}.${name}` : name;
};

export const toAttributeLeaves = (
  attributes: ReadonlyArray<QueryAttributeLike> | null | undefined,
): ReadonlyArray<AttributeLeaf> => {
  if (!attributes) return [];
  // Non-filterable columns stay in the catalogue: the field picker offers
  // them as primary fields, so metadata consumers (labels, type inference,
  // level support) must still resolve them. Pickability is scoped at the
  // rule attribute picker via the `filterable` flag.
  return attributes.map((attr) => {
    const path = toCanonicalAttributePath(attr.name, attr.tableName);
    const dataType = toDataType(attr.type);
    const parts = path.split(".");
    const tableFromPath = parts[0] ?? "attributes";
    const labelFromPath = parts.at(-1) ?? attr.name;
    const table = attr.tableName ?? tableFromPath;
    const label =
      attr.displayName?.trim() || attr.logicalName?.trim() || labelFromPath || attr.name;
    return {
      filterable: Boolean(attr.filterable),
      path,
      label,
      pillLabel: `${table}: ${label}`,
      database: "Semantic layer",
      table,
      dataType,
      values: dataType === "BOOLEAN" ? BOOLEAN_VALUES : [],
      supportedLevels: attr.supportedLevels ?? [],
    };
  });
};

const toLeavesByPath = (leaves: ReadonlyArray<AttributeLeaf>): Map<string, AttributeLeaf> =>
  new Map(leaves.map((leaf) => [leaf.path, leaf]));

export const normalizeAttributePath = (
  path: string,
  leaves: ReadonlyArray<AttributeLeaf> = [],
): string => {
  const leavesByPath = toLeavesByPath(leaves);
  if (leavesByPath.has(path)) return path;
  const parts = path.split(".").filter(Boolean);
  if (parts.length < 2) return path;
  const tailPath = parts.slice(-2).join(".");
  return leavesByPath.has(tailPath) ? tailPath : path;
};

export const findAttributeByPath = (
  path: string,
  leaves: ReadonlyArray<AttributeLeaf> = [],
): AttributeLeaf | undefined =>
  toLeavesByPath(leaves).get(normalizeAttributePath(path, leaves));

/** AG Grid `getDataPath` shape: [table, label] — two-level tree. The
 * database layer (GL DB / Data fabric / GPX) is kept in the data for
 * backend wiring but is not surfaced in the picker. */
export const getAttributeDataPath = (leaf: AttributeLeaf): string[] => [leaf.table, leaf.label];

const matchesQuery = (leaf: AttributeLeaf, lowered: string): boolean =>
  leaf.label.toLowerCase().includes(lowered) ||
  toSentenceCaseLabel(leaf.label).toLowerCase().includes(lowered) ||
  leaf.pillLabel.toLowerCase().includes(lowered) ||
  leaf.path.toLowerCase().includes(lowered) ||
  leaf.table.toLowerCase().includes(lowered) ||
  toSentenceCaseLabel(leaf.table).toLowerCase().includes(lowered);

export type AttributeFilter = (attr: AttributeLeaf) => boolean;

/** `attributeFilter` lets a consumer scope the catalogue (e.g. by
 * `dataType`) — see RPRT-3081 field-level integration. Defaults to "all". */
export const filterAttributeLeaves = (
  query: string,
  attributeFilter?: AttributeFilter,
  leaves: ReadonlyArray<AttributeLeaf> = [],
): ReadonlyArray<AttributeLeaf> => {
  const trimmed = query.trim().toLowerCase();
  const scoped = attributeFilter ? leaves.filter(attributeFilter) : leaves;
  if (trimmed.length === 0) return scoped;
  return scoped.filter((leaf) => matchesQuery(leaf, trimmed));
};
